import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion } from 'framer-motion';
import { Plus, Search, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { useAuthStore } from '@/store/authStore';
import { useProfileStore, useCurrenciesStore } from '@flows.cash/core';

// Currency name and symbol mapping for all supported currencies
const CURRENCY_DETAILS: Record<string, { name: string, symbol: string }> = {
  // Fiat Currencies
  USD: { name: 'US Dollar', symbol: '$' },
  EUR: { name: 'Euro', symbol: '€' },
  GBP: { name: 'British Pound', symbol: '£' },
  JPY: { name: 'Japanese Yen', symbol: '¥' },
  CAD: { name: 'Canadian Dollar', symbol: 'C$' },
  AUD: { name: 'Australian Dollar', symbol: 'A$' },
  CHF: { name: 'Swiss Franc', symbol: 'Fr' },
  CNY: { name: 'Chinese Yuan', symbol: '¥' },
  HKD: { name: 'Hong Kong Dollar', symbol: 'HK$' },
  SGD: { name: 'Singapore Dollar', symbol: 'S$' },
  KRW: { name: 'South Korean Won', symbol: '₩' },
  INR: { name: 'Indian Rupee', symbol: '₹' },
  IDR: { name: 'Indonesian Rupiah', symbol: 'Rp' },
  MYR: { name: 'Malaysian Ringgit', symbol: 'RM' },
  PHP: { name: 'Philippine Peso', symbol: '₱' },
  THB: { name: 'Thai Baht', symbol: '฿' },
  VND: { name: 'Vietnamese Dong', symbol: '₫' },
  TWD: { name: 'Taiwan Dollar', symbol: 'NT$' },
  // Middle Eastern Currencies
  TRY: { name: 'Turkish Lira', symbol: '₺' },
  SAR: { name: 'Saudi Riyal', symbol: 'ر.س' },
  AED: { name: 'UAE Dirham', symbol: 'د.إ' },
  QAR: { name: 'Qatari Riyal', symbol: 'ر.ق' },
  KWD: { name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  BHD: { name: 'Bahraini Dinar', symbol: '.د.ب' },
  OMR: { name: 'Omani Rial', symbol: 'ر.ع.' },
  // Cryptocurrencies
  BTC: { name: 'Bitcoin', symbol: '₿' },
  ETH: { name: 'Ethereum', symbol: 'Ξ' },
  USDT: { name: 'Tether', symbol: '₮' },
  BNB: { name: 'Binance Coin', symbol: 'BNB' },
  SOL: { name: 'Solana', symbol: 'SOL' },
  XRP: { name: 'Ripple', symbol: 'XRP' },
  ADA: { name: 'Cardano', symbol: '₳' },
  DOGE: { name: 'Dogecoin', symbol: 'Ð' },
  DOT: { name: 'Polkadot', symbol: 'DOT' },
  MATIC: { name: 'Polygon', symbol: 'MATIC' },
  // Precious Metals
  XAU: { name: 'Gold', symbol: 'Au' },
  XAG: { name: 'Silver', symbol: 'Ag' },
  XPT: { name: 'Platinum', symbol: 'Pt' },
  XPD: { name: 'Palladium', symbol: 'Pd' },
  XRH: { name: 'Rhodium', symbol: 'Rh' }
};

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function CurrenciesSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { defaultCurrency, lastUpdate, updateRates, convertAmount } = useCurrencyConversion();
  const { settings, fetchSettings } = useProfileStore();
  const { 
    currencies, 
    fetchCurrencies, 
    addCurrency: addCurrencyToStore, 
    deleteCurrency: deleteCurrencyFromStore,
    setDefaultCurrency 
  } = useCurrenciesStore();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdatingRates, setIsUpdatingRates] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isConfirmSetMainOpen, setIsConfirmSetMainOpen] = useState(false);
  const [selectedCurrencyForMain, setSelectedCurrencyForMain] = useState<string | null>(null);

  // Ensure profile settings are loaded
  useEffect(() => {
    if (user?.id && !settings) {
      fetchSettings(user.id);
    }
  }, [user?.id, settings, fetchSettings]);

  // Fetch user currencies on mount
  useEffect(() => {
    if (!user?.id) return;
    fetchCurrencies(user.id);
  }, [user?.id, fetchCurrencies]);

  // Filter currencies based on search query
  const filteredCurrencies = Object.entries(CURRENCY_DETAILS)
    .filter(([code, details]) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        code.toLowerCase().includes(searchLower) ||
        details.name.toLowerCase().includes(searchLower) ||
        details.symbol.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => a[1].name.localeCompare(b[1].name));

  // Group currencies by type
  const groupedCurrencies = {
    fiat: filteredCurrencies.filter(([code]) => !['BTC', 'ETH', 'USDT', 'BNB', 'SOL', 'XRP', 'ADA', 'DOGE', 'DOT', 'MATIC', 'XAU', 'XAG', 'XPT', 'XPD', 'XRH'].includes(code)),
    crypto: filteredCurrencies.filter(([code]) => ['BTC', 'ETH', 'USDT', 'BNB', 'SOL', 'XRP', 'ADA', 'DOGE', 'DOT', 'MATIC'].includes(code)),
    metals: filteredCurrencies.filter(([code]) => ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'].includes(code))
  };

  const handleUpdateRates = async () => {
    try {
      setIsUpdatingRates(true);
      await updateRates();
      toast.success(t('settings.currencies.ratesUpdated'));
    } catch (error) {
      console.error('Error updating rates:', error);
      toast.error(t('settings.currencies.updateError'));
    } finally {
      setIsUpdatingRates(false);
    }
  };

  const handleAddCurrency = async (code: string) => {
    if (!user?.id) {
      toast.error(t('common.unauthorized'));
      return;
    }

    try {
      const details = CURRENCY_DETAILS[code];
      if (!details) {
        toast.error(t('settings.currencies.invalidCurrency'));
        return;
      }

      const newCurrency = {
        code,
        name: details.name,
        symbol: details.symbol,
        isDefault: false,
        user_id: user.id,
        updated_at: new Date().toISOString()
      };

      await addCurrencyToStore(user.id, newCurrency);
      
      setIsAddModalOpen(false);
      toast.success(t('settings.currencies.addSuccess'));
    } catch (error) {
      console.error('Error adding currency:', error);
      toast.error(t('settings.currencies.addError'));
    }
  };

  const handleRemoveCurrency = async (currencyId: string, code: string) => {
    if (!user?.id) {
      toast.error(t('common.unauthorized'));
      return;
    }

    try {
      if (code === defaultCurrency) {
        toast.error(t('settings.currencies.cantRemoveDefault'));
        return;
      }

      await deleteCurrencyFromStore(user.id, currencyId);
      
      toast.success(t('settings.currencies.removeSuccess'));
    } catch (error) {
      console.error('Error removing currency:', error);
      toast.error(t('settings.currencies.removeError'));
    }
  };

  const handleSetAsMain = async (currencyId: string) => {
    const currency = currencies.find(c => c.id === currencyId);
    if (!currency) return;
    setSelectedCurrencyForMain(currencyId);
    setIsConfirmSetMainOpen(true);
  };

  const confirmSetAsMain = async () => {
    if (!user?.id || !selectedCurrencyForMain) return;

    try {
      await setDefaultCurrency(user.id, selectedCurrencyForMain);
      
      setIsConfirmSetMainOpen(false);
      toast.success(t('settings.currencies.mainCurrencyChanged'));
    } catch (error) {
      console.error('Error setting default currency:', error);
      toast.error(t('settings.currencies.defaultChangeError'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary dark:text-text-dark-primary">{t('settings.currencies.title')}</h2>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">{t('settings.currencies.description')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleUpdateRates}
            disabled={isUpdatingRates}
            className="border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80"
          >
            <RefreshCw className={cn(
              "mr-2 h-4 w-4",
              isUpdatingRates && "animate-spin"
            )} />
            {t('settings.currencies.updateRates')}
          </Button>
          <Button
            onClick={() => setIsAddModalOpen(true)}
            size="sm"
            className="bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('settings.currencies.addCurrency')}
          </Button>
        </div>
      </div>

      {lastUpdate && (
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {t('settings.currencies.lastUpdate', { date: new Date(lastUpdate).toLocaleString() })}
        </p>
      )}

      <div className="grid gap-4">
        {currencies.map((currency) => (
          <div
            key={currency.id}
            className="flex items-center justify-between p-4 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150"
          >
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-lg font-semibold text-primary dark:text-primary-dark">
                  {currency.symbol}
                </span>
              </div>
              <div>
                <p className="font-medium text-text-primary dark:text-text-dark-primary">{currency.name}</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary">{currency.code}</p>
                  {currency.code !== defaultCurrency && (
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      • 1 {currency.code}{(() => {
                        const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
                        return preciousMetals.includes(currency.code) ? '/g' : '';
                      })()} = {convertAmount(1, currency.code, defaultCurrency).toFixed(6)} {defaultCurrency}{(() => {
                        const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
                        return preciousMetals.includes(defaultCurrency) ? '/g' : '';
                      })()}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currency.code === defaultCurrency ? (
                <span className="text-sm text-primary dark:text-primary-dark font-medium">
                  {t('settings.currencies.defaultCurrency')}
                </span>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetAsMain(currency.id)}
                    className="border-border dark:border-border-dark"
                  >
                    {t('settings.currencies.setAsMain')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCurrency(currency.id, currency.code)}
                    className="hover:bg-error/10 hover:text-destructive"
                  >
                    {t('actions.remove')}
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[425px] bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-text-primary dark:text-text-dark-primary">{t('settings.currencies.addCurrency')}</DialogTitle>
            <DialogDescription className="text-text-secondary dark:text-text-dark-secondary">
              {t('settings.currencies.selectCurrency')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
              <Input 
                placeholder={t('settings.currencies.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-2.5 text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <ScrollArea className="h-[300px]">
              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="space-y-4"
              >
                {/* Fiat Currencies */}
                {groupedCurrencies.fiat.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                      {t('settings.currencies.fiatCurrencies')}
                    </h4>
                    {groupedCurrencies.fiat.map(([code, details]) => (
                      <motion.button
                        key={code}
                        variants={item}
                        onClick={() => handleAddCurrency(code)}
                        className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-primary/5 dark:hover:bg-primary-dark/10 text-text-primary dark:text-text-dark-primary"
                        disabled={currencies.some(c => c.code === code)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{details.symbol}</span>
                          <span>{details.name}</span>
                        </div>
                        <span className="text-sm text-text-secondary dark:text-text-dark-secondary">{code}</span>
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Cryptocurrencies */}
                {groupedCurrencies.crypto.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                      {t('settings.currencies.cryptocurrencies')}
                    </h4>
                    {groupedCurrencies.crypto.map(([code, details]) => (
                      <motion.button
                        key={code}
                        variants={item}
                        onClick={() => handleAddCurrency(code)}
                        className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-primary/5 dark:hover:bg-primary-dark/10 text-text-primary dark:text-text-dark-primary"
                        disabled={currencies.some(c => c.code === code)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{details.symbol}</span>
                          <span>{details.name}</span>
                        </div>
                        <span className="text-sm text-text-secondary dark:text-text-dark-secondary">{code}</span>
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Precious Metals */}
                {groupedCurrencies.metals.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                      {t('settings.currencies.preciousMetals')}
                    </h4>
                    {groupedCurrencies.metals.map(([code, details]) => (
                      <motion.button
                        key={code}
                        variants={item}
                        onClick={() => handleAddCurrency(code)}
                        className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-primary/5 dark:hover:bg-primary-dark/10 text-text-primary dark:text-text-dark-primary"
                        disabled={currencies.some(c => c.code === code)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{details.symbol}</span>
                          <span>{details.name}</span>
                        </div>
                        <span className="text-sm text-text-secondary dark:text-text-dark-secondary">{code}</span>
                      </motion.button>
                    ))}
                  </div>
                )}
              </motion.div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsAddModalOpen(false)}
              className="border-border dark:border-border-dark"
            >
              {t('actions.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isConfirmSetMainOpen} onOpenChange={setIsConfirmSetMainOpen}>
        <DialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-text-primary dark:text-text-dark-primary">{t('settings.currencies.confirmSetMain')}</DialogTitle>
            <DialogDescription className="text-text-secondary dark:text-text-dark-secondary">
              {t('settings.currencies.confirmSetMainDescription', { 
                code: currencies.find(c => c.id === selectedCurrencyForMain)?.code || '' 
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsConfirmSetMainOpen(false)}
              className="border-border dark:border-border-dark"
            >
              {t('settings.currencies.cancel')}
            </Button>
            <Button
              onClick={confirmSetAsMain}
              className="bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
            >
              {t('settings.currencies.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}